import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";

// Briefly suppress duplicate "slow down" toasts when several queries 429 at once.
let lastRateLimitToastAt = 0;

function notifyRateLimited(retryAfterSec: number) {
  const now = Date.now();
  if (now - lastRateLimitToastAt < 5_000) return;
  lastRateLimitToastAt = now;
  const wait = Math.max(1, Math.ceil(retryAfterSec));
  toast({
    title: "Slow down a moment",
    description:
      wait < 90
        ? `Too many requests — please retry in about ${wait}s.`
        : `Daily request limit reached — please try again later.`,
    variant: "destructive",
  });
}

async function handleRateLimit(res: Response): Promise<void> {
  if (res.status !== 429) return;
  const retryHeader = Number(res.headers.get("Retry-After"));
  let retry = Number.isFinite(retryHeader) ? retryHeader : 0;
  // Clone so the original res is still readable by callers.
  try {
    const body = await res.clone().json();
    if (typeof body?.resetIn === "number") retry = body.resetIn;
  } catch {
    /* body might not be JSON; that's fine */
  }
  notifyRateLimited(retry || 30);
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    if (res.status === 429) await handleRateLimit(res);
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  if (res.status === 429) await handleRateLimit(res);
  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    if (res.status === 429) await handleRateLimit(res);
    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
